import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import FilterSidebar from "@/pages/category/components/FilterSidebar";
import Pagination from "@/pages/category/components/Pagination";
import { filterCatalog, getGenresByType, typeLabel } from "@/lib/catalog";
import type { MediaType } from "@/lib/types";

const sortOptions = [
  { value: "latest", label: "最新上线" },
  { value: "hot", label: "最热播放" },
  { value: "rating", label: "评分最高" },
];

const sizeOptions = [6, 12, 24, 36];

export default function Category() {
  const { type: rawType } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilter, setShowFilter] = useState(false);

  const isValid = rawType === "movie" || rawType === "tv";
  const type = (isValid ? rawType : "movie") as MediaType;

  const genre = searchParams.get("genre") || "all";
  const region = searchParams.get("region") || "全部";
  const year = searchParams.get("year") || "全部";
  const sort = searchParams.get("sort") || "latest";
  const size = Number(searchParams.get("size")) || 12;
  const page = Number(searchParams.get("page")) || 1;

  const genreLabel = getGenresByType(type).find((g) => g.key === genre)?.label;
  const typeText = typeLabel(type);

  const pageTitle = genreLabel
    ? `${genreLabel}${typeText} - ${typeText} - 金桔影院`
    : `${typeText} - 金桔影院`;
  const pageDescription = genreLabel
    ? `金桔影院${genreLabel}${typeText}频道，汇集最新${genreLabel}${typeText}推荐，可按地区、年份筛选，查看作品详情与看点介绍。`
    : `金桔影院${typeText}频道，精选最新最热${typeText}作品，支持按类型、地区、年份筛选，帮助你快速找到想看的${typeText}。`;

  useSeo({
    title: pageTitle,
    description: pageDescription,
    keywords: `金桔影院,${typeText}推荐,${genreLabel || typeText},最新${typeText},${genreLabel || "影视"}排行`,
    canonicalPath: `/category/${type}`,
  });

  const results = useMemo(
    () => filterCatalog({ type, genre, region, year, sort }),
    [type, genre, region, year, sort],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / size));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * size, currentPage * size);

  const updateParams = (patch: Record<string, string>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    if (resetPage) next.delete("page");
    setSearchParams(next);
  };

  const handleFilter = (key: "genre" | "region" | "year", value: string) => {
    updateParams({ [key]: value === "all" || value === "全部" ? "" : value });
  };

  const handleReset = () => setSearchParams(new URLSearchParams());

  if (!isValid) {
    return (
      <div className="mx-auto w-full max-w-8xl px-4 py-20 md:px-6">
        <EmptyState title="分类不存在" description="你访问的分类页面不存在，请从导航重新选择。" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-8xl px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500 md:text-sm">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${type}`} className="transition-colors hover:text-primary-600">
          {typeText}
        </Link>
        {genreLabel && (
          <>
            <i className="ri-arrow-right-s-line"></i>
            <span className="font-semibold text-primary-600">{genreLabel}</span>
          </>
        )}
      </nav>

      <div className="mt-4 rounded-lg bg-gradient-to-r from-primary-500 to-primary-400 px-5 py-6 md:px-8 md:py-8">
        <h1 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
          {genreLabel ? `${genreLabel}${typeText}` : `精选${typeText}`}
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-background-50/90">
          {genreLabel
            ? `这里汇集了金桔影院最值得一看的${genreLabel}${typeText}，支持按地区与年份进一步筛选。`
            : `在这里浏览金桔影院收录的全部${typeText}作品，使用左侧筛选与排序，快速定位你想看的内容。`}
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
        <div className="hidden lg:block">
          <div className="lg:sticky lg:top-24">
            <FilterSidebar
              type={type}
              genre={genre}
              region={region}
              year={year}
              onSelect={handleFilter}
              onReset={handleReset}
            />
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowFilter((v) => !v)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-semibold text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line"></i>
                筛选
              </button>
              <p className="text-sm text-foreground-600">
                共找到 <span className="font-bold text-primary-600">{results.length}</span> 部{typeText}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-1.5 text-sm text-foreground-500">
                排序
                <select
                  value={sort}
                  onChange={(e) => updateParams({ sort: e.target.value })}
                  className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-2 py-1.5 text-sm font-semibold text-foreground-800 outline-none"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-1.5 text-sm text-foreground-500">
                每页
                <select
                  value={size}
                  onChange={(e) => updateParams({ size: e.target.value })}
                  className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-2 py-1.5 text-sm font-semibold text-foreground-800 outline-none"
                >
                  {sizeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}条
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {showFilter && (
            <div className="mt-4 lg:hidden">
              <FilterSidebar
                type={type}
                genre={genre}
                region={region}
                year={year}
                onSelect={handleFilter}
                onReset={handleReset}
              />
            </div>
          )}

          {pageItems.length > 0 ? (
            <>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 xl:grid-cols-4">
                {pageItems.map((item) => (
                  <MovieCard key={item.id} item={item} />
                ))}
              </div>
              <Pagination
                current={currentPage}
                totalPages={totalPages}
                onChange={(nextPage) => {
                  updateParams({ page: String(nextPage) }, false);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </>
          ) : (
            <div className="mt-5">
              <EmptyState
                title={`没有符合条件的${typeText}`}
                description="当前筛选组合下暂无结果，试试放宽地区或年份条件。"
                onReset={handleReset}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}